import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	Edit,
	Eye,
	Loader2,
	MoreHorizontal,
	Plus,
} from "lucide-react";
import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useTRPC } from "@/integrations/trpc/react";
import { useAuth } from "@/lib/auth";
import { getStatusColor, getStatusLabel } from "@/lib/utils";

export const Route = createFileRoute("/solicitations/")({
	component: SolicitationsPage,
});

function SolicitationsPage() {
	const { hasPermission, user } = useAuth();
	const trpc = useTRPC();

	const { data: solicitations, isLoading } = useQuery(
		trpc.solicitations.list.queryOptions({
			buyerOrgId: user?.organizationId ?? "",
			limit: 100,
			offset: 0,
		}),
	);

	// Helper to get effective status (handles scheduled vs open vs awaiting_decision)
	const getEffectiveStatus = (status: string, publishDate: string | null, dueDate: string | null): string => {
		if (status === "open") {
			// Check if scheduled (publish_date in future)
			if (publishDate && new Date(publishDate) > new Date()) {
				return "scheduled";
			}
			// Check if awaiting decision (due_date in the past)
			if (dueDate && new Date(dueDate) <= new Date()) {
				return "awaiting_decision";
			}
		}
		return status;
	};

	// Only draft status is editable
	const canEdit = (status: string, publishDate: string | null) => {
		return status === "draft";
	};

	if (isLoading) {
		return (
			<DashboardLayout>
				<div className="flex items-center justify-center h-64">
					<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
				</div>
			</DashboardLayout>
		);
	}

	return (
		<DashboardLayout>
			<div className="space-y-6">
				<div className="flex items-center justify-between">
					<div>
						<h1 className="text-3xl font-bold tracking-tight">Solicitations</h1>
						<p className="text-muted-foreground">
							Manage your procurement solicitations
						</p>
					</div>
					{hasPermission("edit") && (
						<Button asChild>
							<Link to="/solicitations/new">
								<Plus className="mr-2 h-4 w-4" />
								New Solicitation
							</Link>
						</Button>
					)}
				</div>

				{/* Table */}
				<div className="border rounded-lg">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Title</TableHead>
								<TableHead>Type</TableHead>
								<TableHead>Status</TableHead>
								<TableHead>Due Date</TableHead>
								<TableHead className="text-right">Actions</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{(solicitations ?? []).map((sol) => {
								const effectiveStatus = getEffectiveStatus(sol.status, sol.publish_date, sol.due_date);
								return (
								<TableRow key={sol.id}>
									<TableCell>
										<Link
											to="/solicitations/$id"
											params={{ id: sol.id }}
											className="font-medium hover:underline"
										>
											{sol.title}
										</Link>
									</TableCell>
									<TableCell>
										<Badge variant="outline">{sol.type}</Badge>
									</TableCell>
									<TableCell>
										<Badge className={getStatusColor(effectiveStatus)}>
											{getStatusLabel(effectiveStatus)}
										</Badge>
									</TableCell>
									<TableCell>
										{sol.due_date
											? new Date(sol.due_date).toLocaleDateString()
											: "-"}
									</TableCell>
									<TableCell className="text-right">
										<DropdownMenu>
											<DropdownMenuTrigger asChild>
												<Button variant="ghost" size="icon">
													<MoreHorizontal className="h-4 w-4" />
												</Button>
											</DropdownMenuTrigger>
											<DropdownMenuContent align="end">
												<DropdownMenuItem asChild>
													<Link to="/solicitations/$id" params={{ id: sol.id }}>
														<Eye className="mr-2 h-4 w-4" />
														View Details
													</Link>
												</DropdownMenuItem>
												{hasPermission("edit") && canEdit(sol.status, sol.publish_date) && (
													<DropdownMenuItem asChild>
														<Link
															to="/solicitations/$id/edit"
															params={{ id: sol.id }}
														>
															<Edit className="mr-2 h-4 w-4" />
															Edit
														</Link>
													</DropdownMenuItem>
												)}
											</DropdownMenuContent>
										</DropdownMenu>
									</TableCell>
								</TableRow>
							);
							})}
							{(solicitations ?? []).length === 0 && (
								<TableRow>
									<TableCell
										colSpan={5}
										className="text-center py-8 text-muted-foreground"
									>
										No solicitations found
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</div>
			</div>
		</DashboardLayout>
	);
}
